import FormField from 'components/form-field/form-field';
import { fr } from 'date-fns/locale';
import { Formik } from 'formik';
import { ListHolidayItem } from 'generated/api-client';
import {
  useCreateHoliday,
  useUpdateHoliday,
} from 'generated/api-client/@tanstack/react-query.gen';
import { holidayFormValidationSchema } from 'modules/holiday/utils/holidayFormValidationSchema';
import React, { FunctionComponent } from 'react';
import { Button, Form, Modal } from 'semantic-ui-react';
import { ToastError, ToastSuccess } from 'utils';
import { toCalendarDate, todayIn } from 'utils/date';

type HolidayFormFields = {
  startDate: Date | null;
  endDate: Date | null;
};

type Props = {
  open: boolean;
  viewId: number;
  holiday?: ListHolidayItem;
  viewHolidays: ListHolidayItem[];
  onClose: () => void;
  onSaved: () => void;
};

// The date pickers store UTC midnight, so the initial values must match.
const toFormDate = (day: string): Date => new Date(`${day}T00:00:00Z`);

export const HolidayFormModal: FunctionComponent<Props> = ({
  open,
  viewId,
  holiday,
  viewHolidays,
  onClose,
  onSaved,
}) => {
  const { mutateAsync: createHoliday } = useCreateHoliday();
  const { mutateAsync: updateHoliday } = useUpdateHoliday();

  const today = todayIn('Europe/Paris');
  // Local midnight of the Paris day, for the date pickers' lower bound.
  const firstSelectableDay = new Date(`${today}T00:00:00`);

  const startedBeforeToday = holiday ? holiday.startDate < today : false;

  return (
    <Formik<HolidayFormFields>
      enableReinitialize
      initialValues={{
        startDate: holiday ? toFormDate(holiday.startDate) : null,
        endDate: holiday ? toFormDate(holiday.endDate) : null,
      }}
      validationSchema={holidayFormValidationSchema(viewHolidays, holiday?.id)}
      onSubmit={async (
        { startDate, endDate },
        { resetForm, setSubmitting },
      ) => {
        if (!startDate || !endDate) return;

        const body = {
          startDate: toCalendarDate(startDate),
          endDate: toCalendarDate(endDate),
        };

        try {
          if (holiday) {
            await updateHoliday({ path: { holidayId: holiday.id }, body });
            ToastSuccess('Congé', 'Congé modifié');
          } else {
            await createHoliday({ body: { ...body, viewId } });
            ToastSuccess('Congé', 'Congé créé');
          }
          onSaved();
          resetForm();
          onClose();
        } catch {
          ToastError('Erreur', "Impossible d'enregistrer le congé");
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        values,
        isSubmitting,
        handleSubmit,
        handleReset,
        submitForm,
        resetForm,
      }) => {
        const close = (): void => {
          resetForm();
          onClose();
        };

        // An ongoing holiday starts in the past, but its end cannot move before today.
        const firstSelectableEnd =
          values.startDate && values.startDate > firstSelectableDay
            ? values.startDate
            : firstSelectableDay;

        return (
          <Modal
            open={open}
            onClose={close}
            closeOnDimmerClick={false}
            closeOnEscape={false}
            size="tiny"
            closeIcon
          >
            <Modal.Header>
              {holiday ? 'Édition du congé' : 'Nouveau congé'}
            </Modal.Header>
            <Modal.Content>
              <Form onSubmit={handleSubmit} onReset={handleReset}>
                <FormField
                  label="Date de début"
                  type="date"
                  name="startDate"
                  values={values}
                  dateFormat="dd/MM/yyyy"
                  placeholder="jj/mm/aaaa"
                  locale={fr}
                  minDate={firstSelectableDay}
                  noFastField
                  disabled={startedBeforeToday}
                  helpText={
                    startedBeforeToday
                      ? 'Ce congé a commencé, sa date de début ne peut plus être modifiée'
                      : undefined
                  }
                  required
                />
                <FormField
                  label="Date de fin"
                  type="date"
                  name="endDate"
                  values={values}
                  dateFormat="dd/MM/yyyy"
                  placeholder="jj/mm/aaaa"
                  locale={fr}
                  minDate={firstSelectableEnd}
                  noFastField
                  required
                />
              </Form>
            </Modal.Content>
            <Modal.Actions>
              <Button type="button" onClick={close}>
                Annuler
              </Button>
              <Button
                type="button"
                primary
                loading={isSubmitting}
                disabled={isSubmitting}
                onClick={(): void => {
                  submitForm();
                }}
              >
                Enregistrer
              </Button>
            </Modal.Actions>
          </Modal>
        );
      }}
    </Formik>
  );
};
